<script setup lang="ts">
import type { Locale, TextareaFieldConfig } from '@/forms/types'

const props = defineProps<{
  field: TextareaFieldConfig
  locale: Locale
  modelValue: string
  error?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const errorId = `${props.field.id}-error`
const hintId = `${props.field.id}-hint`
</script>

<template>
  <div class="field" :class="{ 'has-error': !!error }">
    <label class="field-label" :for="field.id">
      {{ field.label[locale] }}
      <span v-if="field.required" aria-hidden="true" class="req">*</span>
    </label>
    <p v-if="field.description" :id="hintId" class="field-hint">{{ field.description[locale] }}</p>
    <textarea
      :id="field.id"
      class="field-input"
      :value="modelValue"
      :placeholder="field.placeholder?.[locale] ?? ''"
      :required="field.required"
      :aria-required="field.required ? 'true' : undefined"
      :aria-invalid="error ? 'true' : undefined"
      :aria-describedby="
        [error ? errorId : '', field.description ? hintId : ''].filter(Boolean).join(' ') ||
        undefined
      "
      rows="5"
      @input="emit('update:modelValue', ($event.target as HTMLTextAreaElement).value)"
    />
    <p v-if="error" :id="errorId" class="field-error" role="alert">{{ error }}</p>
  </div>
</template>

<style scoped>
.field {
  margin-bottom: var(--space-5);
}
textarea {
  resize: vertical;
  min-height: 6rem;
  font-family: inherit;
  line-height: var(--line-height-base);
}
</style>
